import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import { authService } from "@/services/authService";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // null = loading/unknown
  // false = not logged in
  // object = logged-in user

  const [loading, setLoading] = useState(true);

  /**
   * =====================================================
   * Persist Authentication
   * =====================================================
   */
  const persist = useCallback((token, account) => {
    if (!token) {
      throw new Error(
        "Authentication token was not returned by the server."
      );
    }

    localStorage.setItem("cet_token", token);

    setUser(account);
  }, []);

  /**
   * =====================================================
   * Logout
   * =====================================================
   */
  const logout = useCallback(() => {
    localStorage.removeItem("cet_token");

    setUser(false);
  }, []);

  /**
   * =====================================================
   * Load Current User
   * =====================================================
   */
  const loadMe = useCallback(async () => {
    const token =
      localStorage.getItem("cet_token");

    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }

    try {
      const me =
        await authService.me();

      /**
       * Backend may return user directly
       * or inside data.
       */
      const account =
        me?.user ||
        me?.candidate ||
        me?.admin ||
        me?.data ||
        me;

      setUser(account);
    } catch (error) {
      console.error(
        "Auth session restore failed:",
        error
      );

      localStorage.removeItem(
        "cet_token"
      );

      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * =====================================================
   * Initial Auth Check
   * =====================================================
   */
  useEffect(() => {
    loadMe();
  }, [loadMe]);

  /**
   * =====================================================
   * Candidate Register
   * =====================================================
   */
  const register = async (payload) => {
    const data =
      await authService.register(payload);

    /**
     * Make sure backend returned token.
     */
    if (!data?.token) {
      console.error(
        "Register response:",
        data
      );

      throw new Error(
        "Registration successful, but authentication token was not returned."
      );
    }

    /**
     * Make sure candidate data exists.
     */
    if (!data?.candidate) {
      console.error(
        "Register response:",
        data
      );

      throw new Error(
        "Registration successful, but candidate data was not returned."
      );
    }

    persist(data.token, {
      ...data.candidate,
      role: "candidate",
    });

    return data;
  };

  /**
   * =====================================================
   * Candidate Login
   * =====================================================
   */
  const login = async (payload) => {
    const data =
      await authService.login(payload);

    if (!data?.token) {
      console.error(
        "Login response:",
        data
      );

      throw new Error(
        "Login failed because authentication token was not returned."
      );
    }

    if (!data?.candidate) {
      console.error(
        "Login response:",
        data
      );

      throw new Error(
        "Login failed because candidate data was not returned."
      );
    }

    persist(data.token, {
      ...data.candidate,
      role: "candidate",
    });

    return data;
  };

  /**
   * =====================================================
   * Admin Login
   * =====================================================
   */
  const adminLogin = async (payload) => {
    const data =
      await authService.adminLogin(
        payload
      );

    if (!data?.token) {
      console.error(
        "Admin login response:",
        data
      );

      throw new Error(
        "Admin login failed because authentication token was not returned."
      );
    }

    if (!data?.admin) {
      console.error(
        "Admin login response:",
        data
      );

      throw new Error(
        "Admin login failed because admin data was not returned."
      );
    }

    persist(data.token, {
      ...data.admin,
      role: "admin",
    });

    return data;
  };

  /**
   * =====================================================
   * Context
   * =====================================================
   */
  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        register,
        login,
        adminLogin,
        logout,
        refresh: loadMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * =====================================================
 * useAuth Hook
 * =====================================================
 */
export function useAuth() {
  const ctx =
    useContext(AuthContext);

  if (!ctx) {
    throw new Error(
      "useAuth must be used within AuthProvider"
    );
  }

  return ctx;
}